import { useCallback, useEffect, useRef, useState } from 'react';

import './ForeignDns.css';
import { ForeignDnsRunDetail } from './ForeignDnsRunDetail';
import { fdApi, fdErrorMessage, type FdRun, type FdRunStatus } from './foreignDnsApi';

/** The wizard is eight steps; the run row carries the pointer as current_step. */
const TOTAL_STEPS = 8;

const NAME_INPUT_ID = 'fd-run-name';
const NAME_HINT_ID = 'fd-run-name-hint';

const TOAST_MS = 6000;

/**
 * Run status to badge variant, in the badge colours the whole feature shares:
 * sage for a finished run, blue for one in flight (the app-wide running colour
 * - .trf-status--running and .srm-status--running are both blue), muted for a
 * run that has not started or was abandoned. `cancelled` is deliberate operator
 * action, not a failure, so it is not rose.
 */
const STATUS_BADGE: Record<FdRunStatus, string> = {
  draft: 'muted',
  in_progress: 'info',
  completed: 'ok',
  cancelled: 'muted',
};

const STATUS_LABEL: Record<FdRunStatus, string> = {
  draft: 'draft',
  in_progress: 'in progress',
  completed: 'completed',
  cancelled: 'cancelled',
};

interface Toast {
  type: 'success' | 'error' | 'info';
  message: string;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ForeignDnsRuns(): JSX.Element {
  const [runs, setRuns] = useState<FdRun[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  /**
   * One toast at a time, one timer at a time - the pattern already used in
   * ForeignDnsRunDetail. The previous timer has to be CANCELLED, not outlived:
   * a toast raised near the end of an earlier toast's window is otherwise wiped
   * by that earlier timer firing. Cleared on unmount too, so a queued callback
   * cannot fire into a dead component.
   */
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((type: Toast['type'], message: string) => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    setToast({ type, message });
    toastTimer.current = window.setTimeout(() => {
      toastTimer.current = null;
      setToast(null);
    }, TOAST_MS);
  }, []);

  useEffect(
    () => () => {
      if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  /**
   * A failed load is NOT an empty list, and is not a toast either.
   *
   * As a toast it was both wrong and short-lived: "No runs yet." reads as "there
   * is nothing here", which invites the operator to start a duplicate run for a
   * sheet that already has one - and the 6s toast that said otherwise had
   * already been overwritten by createRun's own success message. It is state,
   * it persists until a load succeeds, and `runs` keeps its last good contents
   * so a failed refresh does not blank a list that was on screen.
   */
  const refreshRuns = useCallback(async (): Promise<void> => {
    try {
      setRuns(await fdApi.listRuns());
      setListError(null);
    } catch (err) {
      setListError(fdErrorMessage(err));
    } finally {
      setLoaded(true);
    }
  }, []);

  // The list does not poll. Nothing on it changes without an operator acting,
  // and the only live state in this feature (a stage mid-flight) is polled by
  // the detail view while it is open. The list refetches on mount, after a
  // create, and when the operator comes back from a run.
  useEffect(() => {
    void refreshRuns();
  }, [refreshRuns]);

  async function createRun(): Promise<void> {
    const trimmed = name.trim();
    if (trimmed === '') return;
    setCreating(true);
    try {
      const run = await fdApi.createRun(trimmed);
      setName('');
      await refreshRuns();
      showToast('success', `Run created: ${run.name}`);
    } catch (err) {
      showToast('error', fdErrorMessage(err));
    } finally {
      setCreating(false);
    }
  }

  if (openRunId) {
    return (
      <ForeignDnsRunDetail
        runId={openRunId}
        onBack={() => {
          setOpenRunId(null);
          void refreshRuns();
        }}
      />
    );
  }

  return (
    <div className="fd">
      <div className="fd-scroll">
        <header className="fd-header">
          <h1 className="fd-title">Foreign CF DNS</h1>
          <p className="fd-sub">
            Apply our standard PI record set to domains that live in a{' '}
            <strong>customer's own Cloudflare account</strong>. Start a run per sheet, paste the
            customer's Cloudflare API token once, and the wizard walks the eight steps: key,
            domains, purge, forwarding, basic DNS, Google verification codes, DKIM, and a final
            overview with a per-domain record editor. The token is held only for the life of the
            run, is never shown back, and is destroyed the moment you finish or cancel.
          </p>
        </header>

        {listError && (
          <div className="fd-toast fd-toast--error">
            Could not load the run list: {listError}
          </div>
        )}
        {toast && <div className={`fd-toast fd-toast--${toast.type}`}>{toast.message}</div>}

        <section className="fd-card">
          <div className="fd-card-header">
            <h2 className="fd-card-title">Start a run</h2>
          </div>
          <div className="fd-card-body">
            {/* The hint is deliberately OUTSIDE the label and referenced with
                aria-describedby. Nested inside it, the computed accessible name
                becomes the label and the whole hint run together. */}
            <div className="fd-field">
              <label className="fd-field-label" htmlFor={NAME_INPUT_ID}>
                Run name
              </label>
              <input
                id={NAME_INPUT_ID}
                aria-describedby={NAME_HINT_ID}
                className="fd-input"
                type="text"
                value={name}
                maxLength={300}
                placeholder="acme@example.com recXXXXXXXXXXXXXX 2026-07-27"
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  // `creating` matters here and not only on the button: the
                  // button is disabled mid-create, but Enter bypasses it, and
                  // two quick presses would create two identical draft runs
                  // that nothing downstream can tell apart (names are
                  // deliberately not unique).
                  if (e.key === 'Enter' && !creating) void createRun();
                }}
                spellCheck={false}
              />
              <span className="fd-hint" id={NAME_HINT_ID}>
                Paste the Google Sheet name - customer email + Airtable record ID + date
              </span>
            </div>
            <div className="fd-actions">
              <div className="fd-actions-spacer" />
              <button
                type="button"
                className="fd-btn fd-btn--primary"
                onClick={() => void createRun()}
                disabled={creating || name.trim() === ''}
              >
                {creating ? 'Creating...' : 'Create run'}
              </button>
            </div>
          </div>
        </section>

        <section className="fd-card">
          <div className="fd-card-header">
            <h2 className="fd-card-title">Runs</h2>
            <button
              type="button"
              className="fd-btn fd-btn--ghost fd-btn--small"
              onClick={() => void refreshRuns()}
            >
              Refresh
            </button>
          </div>
          <div className="fd-table-wrap">
            <table className="fd-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Created</th>
                  <th>By</th>
                  <th className="fd-col-center">Status</th>
                  <th className="fd-col-center">Step</th>
                  <th className="fd-col-center" />
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="fd-empty">
                      {!loaded
                        ? 'Loading runs...'
                        : listError !== null
                          ? 'The run list could not be loaded - this is not an empty list. Use Refresh to try again.'
                          : 'No runs yet.'}
                    </td>
                  </tr>
                ) : (
                  runs.map((r) => (
                    // No row-level onClick. A clickable <tr> is mouse-only
                    // unless it grows tabIndex + role + a key handler, and the
                    // row already carries a real Open button that a keyboard
                    // reaches for free. One affordance, no bolt-on.
                    <tr key={r.id}>
                      <td className="fd-cell-mono">{r.name}</td>
                      <td className="fd-cell-faint">{fmtDate(r.created_at)}</td>
                      <td className="fd-cell-faint">
                        {r.created_by}
                        {r.created_by_role !== r.created_by ? ` (${r.created_by_role})` : ''}
                      </td>
                      <td className="fd-col-center">
                        <span className={`fd-badge fd-badge--${STATUS_BADGE[r.status]}`}>
                          {STATUS_LABEL[r.status]}
                        </span>
                      </td>
                      <td className="fd-col-center">
                        {r.current_step} of {TOTAL_STEPS}
                      </td>
                      <td className="fd-col-center">
                        <button
                          type="button"
                          className="fd-btn fd-btn--secondary fd-btn--small"
                          onClick={() => setOpenRunId(r.id)}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
